import { z } from "zod";
import { memberDueStatusSchema } from "./collections.js";
// Vai nhóm dùng CHUNG một enum với mọi bề mặt khác — khai bản thứ hai ở đây là mở đường cho hai danh
// sách vai trôi lệch. Một chiều (payment-board → groups), `groups.ts` không import ngược lại.
import { groupRoleSchema } from "./groups.js";

/* ===========================================================================
 * US-E8.F5-0056 Payment Status Board (@UC0047) — Phase 9 read-only.
 * GET /v1/collections/:id/payment-board — ADMIN/TREASURER/OWNER only (Member → 403).
 *
 * Tổng đã thu / còn thiếu / kỳ vọng (từ member_dues.amountMinor + status) + list member (CÓ số tiền —
 * đây là view quản lý, khác 0047 members-status ẩn tiền). Filter status. amount_minor no-format B1.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/** Filter status trên board (query) — 'all' + các member_due status. */
export const paymentBoardStatusFilterSchema = z.enum([
  "all",
  "paid",
  "awaiting_confirmation",
  "pending",
  "overdue",
  "waived",
  "paid_on_behalf",
]);
export type PaymentBoardStatusFilter = z.infer<
  typeof paymentBoardStatusFilterSchema
>;

/** Query board — filter status optional. */
export const paymentBoardQuerySchema = z
  .object({
    status: paymentBoardStatusFilterSchema.optional(),
  })
  .strict();
export type PaymentBoardQuery = z.infer<typeof paymentBoardQuerySchema>;

/** 1 dòng member trên board (CÓ số tiền — admin view). */
export const paymentBoardMemberSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    memberName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /**
     * 🔴 US-E24.F46-0460 (@UC0060 · @UC9659) — phần ĐÃ ĐÓNG của due này (STRING minor, B1).
     *
     * VÌ SAO CẦN: bảng thu trước đây chỉ trả `amountMinor` (số PHẢI đóng), nên một người đã đóng
     * 1.120.000/1.200.000 không có chỗ nào để client hiện con số đó — họ nằm nguyên trong mục
     * "chưa đóng" như thể chưa trả đồng nào. Bug thật báo cáo 19/08/2026, nhóm `3ea4be49`:
     * sổ cái GHI ĐÚNG cho suất, chỉ màn hình không nói ra.
     *
     * `"0"` khi chưa đóng gì — KHÔNG nullable: thiếu số ≠ số 0, và ở đây luôn có số thật.
     */
    paidAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    status: memberDueStatusSchema,
    /** member_due id — cho tap 'chờ duyệt' → màn duyệt (0044 wiring). */
    memberDueId: z.string().uuid(),
    /**
     * US-E24.F9-0358-S02 (@UC9589) — lần nhắc GẦN NHẤT của CHÍNH due này (ISO), null nếu chưa nhắc.
     * 🔴 DẪN XUẤT từ `outbox_events` (gộp nhắc thủ công + nhắc worker), **KHÔNG** phải cột trên
     * `member_dues` — thêm cột là tạo nguồn thứ hai sẽ trôi khỏi nhau.
     * 🔴 CHỈ phơi ở board của O/A/T. Đây là màn quản lý; member KHÔNG thấy "bạn đã bị nhắc 3 lần"
     * (§7 P5.3 — kênh NHẮC là riêng tư, app không đóng vai kẻ bêu tên).
     */
    lastRemindedAt: z.string().datetime().nullable(),
    /**
     * US-E24.F38-0452 (@UC0060) — THỜI ĐIỂM ĐÓNG của chính due này (ISO), `null` nếu chưa đóng.
     *
     * Cột `member_dues.paid_at` đã tồn tại từ lâu, chỉ chưa bao giờ được phơi ra. Màn "Chi tiết đợt
     * thu" cần nó để trả lời câu hỏi đầu tiên mọi thủ quỹ hỏi: *"ai đã đóng, và đóng hôm nào?"* —
     * thiếu nó thì danh sách "đã đóng" chỉ là một tập tên, không đối chiếu được với sao kê ngân hàng.
     *
     * 🔴 CHỈ ở board của O/A/T, KHÔNG ở `members-status` (kênh XEM của member) — mirror `lastRemindedAt`
     * ngay trên. Giờ-đóng của người khác là dữ liệu quản lý, không phải thứ cả nhóm cần thấy.
     */
    paidAt: z.string().datetime().nullable(),
    /**
     * US-E24.F38-0452 (@UC0060) — VAI của thành viên trong nhóm. Màn chi tiết hiện huy hiệu vai
     * (chủ nhóm / thủ quỹ) cạnh tên; thiếu field này client chỉ có hai đường, cả hai đều sai:
     * bỏ huy hiệu, hoặc gọi thêm một request danh sách thành viên rồi tự ghép — mà ghép ở client thì
     * sai ngay từ trang thứ hai của danh sách phân trang.
     */
    role: groupRoleSchema,
  })
  .strict();
export type PaymentBoardMember = z.infer<typeof paymentBoardMemberSchema>;

/**
 * Response board. Tổng đã thu (paid+paid_on_behalf) / còn thiếu (pending+overdue+awaiting) / kỳ vọng
 * (tổng non-waived). `members` = list đã filter (nếu có status). `expectedMinor` KHÔNG gồm waived
 * (miễn không kỳ vọng thu). Tiền STRING minor unit (B1).
 */
export const paymentBoardResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    currency: z.string().length(3),
    /** Tổng đã thu (paid + paid_on_behalf). */
    collectedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Còn thiếu (pending + overdue + awaiting_confirmation). */
    outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Kỳ vọng thu (tổng non-waived = collected + outstanding). */
    expectedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Đếm theo status (mọi due, không phụ thuộc filter) cho chip filter. */
    counts: z.object({
      paid: z.number().int(),
      awaiting_confirmation: z.number().int(),
      pending: z.number().int(),
      overdue: z.number().int(),
      waived: z.number().int(),
      paid_on_behalf: z.number().int(),
      // US-E16.F4-0185 (@UC9241): đóng một phần (còn nợ dư). Default 0 để response cũ vẫn hợp lệ.
      partially_paid: z.number().int().default(0),
    }),
    members: z.array(paymentBoardMemberSchema),
  })
  .strict();
export type PaymentBoardResponse = z.infer<typeof paymentBoardResponseSchema>;
